import { useEffect, useState } from 'react';
import { Share, X, ArrowDown } from 'lucide-react';

/**
 * iOS-specific PWA install hint.
 *
 * Apple deliberately doesn't expose the `beforeinstallprompt` event,
 * so we can't trigger an install prompt on iOS the way Chrome on
 * Android does. Operators have to do it manually:
 *   Share → Add to Home Screen.
 *
 * This banner appears once per device on iOS Safari (only when NOT
 * already running in standalone mode — `navigator.standalone === true`
 * means the user already installed and launched from the home screen).
 * Dismissal is persisted in localStorage so we don't pester them again.
 *
 * Detection is intentionally conservative: render only when ALL of:
 *   - the platform is iOS (iPhone / iPad / iPod)
 *   - we're inside Safari (not Chrome's CriOS / Firefox's FxiOS,
 *     neither of which can install PWAs anyway — we tell those users
 *     to switch to Safari)
 *   - we're not already in standalone mode
 *   - the user hasn't dismissed before
 */
const DISMISS_KEY = 'zafroi-pwa:ios-install-hint:dismissed';

interface IosNavigator extends Navigator {
  standalone?: boolean;
}

function detectMode(): 'install-hint' | 'wrong-browser' | 'hide' {
  if (typeof window === 'undefined') return 'hide';
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  if (!isIos) return 'hide';
  const standalone = (window.navigator as IosNavigator).standalone === true;
  if (standalone) return 'hide';
  // iOS Chrome/Firefox/Edge — they all wrap WebKit but cannot install
  // PWAs. Steer the operator into Safari instead.
  const isSafari = !/CriOS|FxiOS|EdgiOS/.test(ua) && /Safari/.test(ua);
  return isSafari ? 'install-hint' : 'wrong-browser';
}

export function IosInstallHint() {
  const [mode, setMode] = useState<'install-hint' | 'wrong-browser' | 'hide'>(
    'hide',
  );

  useEffect(() => {
    const detected = detectMode();
    if (detected === 'hide') return;
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      /* private-mode storage blocked — show the banner anyway */
    }
    if (!dismissed) setMode(detected);
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* non-fatal */
    }
    setMode('hide');
  };

  if (mode === 'hide') return null;

  if (mode === 'wrong-browser') {
    return (
      <Banner onDismiss={dismiss}>
        <div className="space-y-1">
          <p className="text-[12.5px] font-medium text-foreground">
            Open in Safari to install
          </p>
          <p className="text-[11.5px] text-muted-foreground">
            iPhone's Chrome / Firefox / Edge can't install web apps. Long-press
            the address bar → "Open in Safari", then we can add this to your
            home screen.
          </p>
        </div>
      </Banner>
    );
  }

  return (
    <Banner onDismiss={dismiss}>
      <div className="space-y-1.5">
        <p className="text-[12.5px] font-medium text-foreground">
          Add Zafroi Ops to your home screen
        </p>
        <p className="text-[11.5px] text-muted-foreground">
          On iPhone, tap{' '}
          <Share className="mx-0.5 inline h-3 w-3 align-text-bottom" />{' '}
          <span className="font-medium text-foreground">Share</span>{' '}
          at the bottom of Safari, scroll down and pick{' '}
          <span className="font-medium text-foreground">Add to Home Screen</span>.
          Launching from the icon gives you a full-screen scanner with no
          browser bars.
        </p>
        <p className="flex items-center gap-1 pt-0.5 text-[10.5px] text-muted-foreground">
          <ArrowDown className="h-2.5 w-2.5" />
          Share button is in the bottom toolbar.
        </p>
      </div>
    </Banner>
  );
}

function Banner({
  onDismiss,
  children,
}: {
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pwa-anim-in mb-3 flex gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3.5 shadow-elev-1"
    >
      <div className="flex-1 min-w-0">{children}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="pwa-tap -mr-1 shrink-0 rounded-full p-1.5 text-muted-foreground active:bg-primary/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
